import { useState, useEffect } from 'react';
import { Menu, ChevronRight, ChevronLeft, LogOut, Bot, Shield, BarChart3, MessagesSquare, MessageSquareMore, BookOpen, Workflow } from 'lucide-react'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@chainlit/react-client';
import { appConfig } from '@/config/appConfig';
import { cn } from '@/lib/utils';

type AdminSection = 'assistants' | 'iam' | 'dashboard' | 'conversation-history' | 'conversation-feedback' | 'knowledge-center' | 'agents';

interface LayoutProps {
  children: React.ReactNode;
  activeSection?: AdminSection;
  onSectionChange?: (section: AdminSection) => void;
  availableSections?: AdminSection[]; // New prop to filter sections
}

export default function Layout({ children, activeSection = 'dashboard', onSectionChange, availableSections }: LayoutProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile && !sidebarOpen) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Helper to check if section should be shown
  const shouldShowSection = (section: AdminSection) => {
    if (!availableSections) return true; // Show all if not filtered
    return availableSections.includes(section);
  };

  return (
    <div className="flex h-screen bg-background">
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-foreground/50"
          onClick={toggleSidebar}
        />
      )}

      <div
        className={cn(
          "transition-all duration-300 ease-in-out h-full z-30 border-r border-border bg-background",
          isMobile ? "fixed" : "relative",
          isMobile && !sidebarOpen ? "w-0" : sidebarOpen ? "w-64" : "w-20"
        )}
      >
        <div className="flex items-center justify-between p-4">
          <h1
            className={cn(
              "font-bold text-xl text-primary cursor-pointer",
              !sidebarOpen && "hidden"
            )}
            onClick={() => navigate('/')}
          >
            {appConfig.appName}
          </h1>
          <button
            onClick={toggleSidebar}
            className="focus:outline-none text-primary"
          >
            {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        <nav className="mt-6">
          <ul>
            {shouldShowSection('dashboard') && (
              <SidebarMenuItem
                icon={<BarChart3 size={20} />}
                text="Dashboard"
                active={activeSection === 'dashboard'}
                collapsed={!sidebarOpen}
                onClick={() => onSectionChange?.('dashboard')}
              />
            )}
            {shouldShowSection('conversation-history') && (
              <SidebarMenuItem
                onClick={() => onSectionChange?.('conversation-history')}
                icon={<MessagesSquare size={20} />}
                text="Conversation History"
                active={activeSection === 'conversation-history'}
                collapsed={!sidebarOpen}
              />
            )}
            {shouldShowSection('conversation-feedback') && (
              <SidebarMenuItem
                onClick={() => onSectionChange?.('conversation-feedback')}
                icon={<MessageSquareMore size={20} />}
                text="Conversation Feedback"
                active={activeSection === 'conversation-feedback'}
                collapsed={!sidebarOpen}
              />
            )}
            {shouldShowSection('knowledge-center') && (
              <SidebarMenuItem
                icon={<BookOpen size={20} />}
                text="Knowledge Center"
                active={activeSection === 'knowledge-center'}
                collapsed={!sidebarOpen}
                onClick={() => onSectionChange?.('knowledge-center')}
              />
            )}
            {shouldShowSection('assistants') && (
              <SidebarMenuItem
                icon={<Bot size={20} />}
                text="Assistants"
                active={activeSection === 'assistants'}
                collapsed={!sidebarOpen}
                onClick={() => onSectionChange?.('assistants')}
              />
            )}
            {shouldShowSection('agents') && (
              <SidebarMenuItem
                icon={<Workflow size={20} />}
                text="Agents"
                active={activeSection === 'agents'}
                collapsed={!sidebarOpen}
                onClick={() => onSectionChange?.('agents')}
              />
            )}
            {shouldShowSection('iam') && (
              <SidebarMenuItem
                icon={<Shield size={20} />}
                text="IAM"
                active={activeSection === 'iam'}
                collapsed={!sidebarOpen}
                onClick={() => onSectionChange?.('iam')}
              />
            )}
            <SidebarMenuItem
              active={false}
              icon={<LogOut size={20} />}
              text="Logout"
              collapsed={!sidebarOpen}
            />
          </ul>
        </nav>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="shadow-sm h-16 flex items-center px-4 bg-background border-b border-border">
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="focus:outline-none text-foreground"
            >
              <Menu size={24} />
            </button>
          )}
          {user && (
            <h2 className="text-xl font-semibold ml-4 text-foreground">
              {`Welcome ${user?.display_name}`}
            </h2>
          )}
        </header>

        <main className="flex-1 overflow-auto p-4 pb-12">
          <div className="rounded-lg shadow p-6 bg-background min-h-full">
            <div className="rounded-lg text-muted-foreground">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarMenuItem({
  icon,
  text,
  active = false,
  collapsed = false,
  onClick,
}: {
  icon: React.ReactNode;
  text: string;
  active: boolean;
  collapsed: boolean;
  onClick?: () => void;
}) {
  return (
    <li
      className={cn(
        "mb-2 transition-colors duration-200",
        onClick && "cursor-pointer",
        active 
          ? "bg-primary text-primary-foreground" 
          : "text-primary hover:bg-primary/20"
      )}
      onClick={onClick}
    >
      <button
        className={cn(
          "flex items-center py-3 w-full transition-colors duration-200 bg-transparent border-none",
          collapsed ? "justify-center px-3" : "px-3"
        )}
        style={{ color: 'inherit' }}
      >
        <span>{icon}</span>
        {!collapsed && <span className="ml-2">{text}</span>}
      </button>
    </li>
  );
}
